<template>
  <div class="detail">
    <div class="detail-con">
      <div class="section1">
        <div class="tips">
          <img src="../../assets/knowledge-center//top1.svg" alt="" />
          <h1 class="heading">
            Digital Marketing for Global Audiences
            <br />
            <span>5 Tips and Best Practices</span>
          </h1>

          <p>
            Social media and online search engines are among the main digital
            channels for merchants to reach new audiences around the world,
            raise brand awareness and drive international demand. In fact, our
            survey found that 75% of online shoppers have purchased from a brand
            outside their market after they engaged with it on social media.
          </p>
          <p>
            However, running effective campaigns across different international
            markets can be challenging. From market selection to setting the
            campaign strategy and scope to determining the media mix and
            adapting ad content to each market, successful campaigns require
            dedicated setup and management rather than simply extending domestic
            efforts. Combining a local approach, based on an understanding of
            the market, with an analysis of the brand’s potential can help
            companies develop an optimal strategy to deliver results.
          </p>
          <p>
            We asked Global-e’s DTC Digital Marketing Team to share some
            insightful tips on running digital campaigns across the world. These
            best practices are drawn from their considerable experience managing
            extensive campaigns in over 25 international markets.
          </p>
        </div>
      </div>
      <div class="section2">
        <div class="words">
          <h1 class="title ti1">#1</h1>
          <div class="ti-con">
            <div class="ti-top">Identify high-potential markets</div>
            <p>
              The first step in launching digital marketing activities in
              international markets is to identify those with high growth
              potential for your brand. To pinpoint these markets, it’s
              important to consider various factors that combine economic
              indicators and market characteristics. We’ve outlined key
              considerations that, collectively, help assess market potential.
            </p>
            <div class="hTi">Brand data</div>
            <ul>
              <li>
                Current market size: the brand's current sales volume in this
                market
              </li>
              <li>
                Brand awareness per market: analyzing brand searches per
                international market compared to brand searches in its domestic
                market
              </li>
              <li>
                Market’s unit economics: market profitability (before marketing
                spend)
              </li>
            </ul>
            <div class="hTi">Market data</div>
            <ul>
              <li>Potential market size: population size of the market</li>
              <li>
                GDP (Gross Domestic Product) per capita: the market’s buying
                power
              </li>
              <li>Internet penetration and e-commerce adoption</li>
              <li>
                Global e-commerce market trends: market growth in the last few
                quarters
              </li>
              <li>
                Local tax and duty threshold and rates: evaluating local tax and
                duty rates on personal imports Some markets, like Brazil, might
                seem promising due to their size and buying power. However, high
                taxes and duties can pose substantial barriers to online
                purchases outside the domestic market. Consequently, launching
                campaigns in a market like Brazil might not achieve the
                anticipated results.
              </li>
              <li>
                Digital marketing benchmarks: media costs (CPM/CPCs) and
                conversation rates by market
              </li>
              <li>
                Competitive landscape and benchmarks: analyzing competitors and
                resellers in the market, compared with benchmark data to
                identify potential opportunities
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="section2 section3">
        <div class="words left-con">
          <h1 class="title">#2</h1>
          <div class="ti-con">
            <div class="ti-top">Find Where Your Local Audience Is</div>
            <p>
              Identify the most popular media channels within your target market
              and include them into your media plan, alongside global paid
              channels such as Meta and Google.
            </p>
            <p>
              In certain countries, local media channels are key players; for
              instance, Naver and KakaoTalk in South Korea, and Line in Japan,
              are very popular, and integral to a local strategy.
            </p>
            <p>
              Additionally, leveraging affiliate and influencer channels can be
              instrumental in reaching local audiences in international markets,
              helping your brand gain recognition, build trust, and scale its
              activities.
            </p>
          </div>
        </div>
        <div class="right-con">
          <h3>Did you know?</h3>
          <img src="../../assets/knowledge-center/talk.svg" alt="" />
          <p>
            KakaoTalk is the leading social network in South Korea, and it is
            used by 93% of smartphone owners in this market.
          </p>
        </div>
      </div>
      <div class="section2 section3">
        <div class="words left-con">
          <h1 class="title">#3</h1>
          <div class="ti-con">
            <div class="ti-top">Adapt your ads to the market</div>
            <p>
              When it comes to international digital marketing campaigns, it's
              important to adapt your ads to resonate with the local culture of
              your target market.
            </p>
            <p>
              Use high-quality human translations; ensure translators are native
              to the market and possess strong copywriting skills to produce
              professional and effective marketing copy. For brands with unique
              or word-play-driven copy, consider transcreation and fully rewrite
              the messaging for local audiences. If a high-quality human
              translation isn't possible, it's better to stick with English.
            </p>
            <p>
              Visuals and text should reflect cultural nuances. For example, in
              the Gulf Markets, creatives are typically more modest, sometimes
              incorporating elements of local culture like a headscarf.
            </p>
            <p>
              To ensure your ads engage effectively, have them reviewed by a
              native of the market who can verify their appeal to the local
              audience.
            </p>
          </div>
        </div>
        <div class="right-con right-img">
          <img src="../../assets/knowledge-center/rimg1.png" alt="" />
        </div>
      </div>
      <div class="section2 section3">
        <div class="words left-con">
          <h1 class="title">#4</h1>
          <div class="ti-con">
            <div class="ti-top">
              Present prices and tailor promotions for local markets
            </div>
            <p>
              Adapt your local pricing and promotions to gain wider market
              relevancy and appeal.
            </p>
            <p>
              Display prices in the local currency within your creatives for
              paid media, email marketing, and other channels. If displaying
              local currency in your marketing is not possible, it is better to
              omit pricing altogether.
            </p>
            <p>
              For catalogue-based ads on Google, Meta, and TikTok, which are a
              highly effective and profitable ad type and an important component
              of an international digital marketing strategy, pricing has to be
              presented in the local currency and match the pricing on your
              website for that market.
            </p>
            <p>
              For multi market campaigns, opt for percentage discounts over
              value discounts to ensure clarity and relevance across various
              markets. For example, use: “25% off” instead of “CA$50 off”.
            </p>
            <p>
              Finally, be aware of local rules and laws governing promotions. In
              France, for example, using the term ‘Sale’ (Soldes) is restricted
              to only during two periods a year. Discounts are permissible
              outside those periods, but you cannot use the word ‘Sale’.
            </p>
          </div>
        </div>
        <div class="right-con right-img">
          <img src="../../assets/knowledge-center/rimg2.png" alt="" />
          <img src="../../assets/knowledge-center/rimg3.png" alt="" />
        </div>
      </div>
      <div class="section2 section3">
        <div class="words left-con">
          <h1 class="title">#5</h1>
          <div class="ti-con">
            <div class="ti-top">
              Leverage local holidays and shopping events
            </div>
            <p>
              Local shopping events and holidays offer significant opportunities
              for sales performance and enhancing brand awareness.
            </p>
            <p>
              Add these local events to your marketing calendar to plan for them
              effectively. If they aren't included in your annual strategy, we
              recommend coordinating at least one month before.
            </p>
            <p>
              ‍Global-e’s e-commerce calendar, featuring popular shopping peaks
              worldwide, is a valuable tool to help you track local events so
              you can schedule your activities throughout the year.
            </p>
            <p>
              ‍Identify the events relevant to your brand, vertical, and
              marketing strategy. For campaigns around specific local dates,
              consider designing dedicated creatives or incorporating relevant
              messages into existing ones.
            </p>
            <p>
              Enhance your local presence and relevance by adapting your
              messaging to acknowledge local holidays and adding respectful
              greetings to your copy, even if you are not planning a promotional
              campaign around the event. For example, when targeting markets
              where Chinese New Year is widely celebrated, you can add a
              relevant greeting like “Happy Lunar New Year!” and incorporate red
              into your creative materials.
            </p>
          </div>
        </div>
        <div class="right-con right-img">
          <img src="../../assets/knowledge-center/rimg4.png" alt="" />
          <img src="../../assets/knowledge-center/rimg5.png" alt="" />
        </div>
      </div>
      <div class="alone">
        <p>
          Navigating the complex landscape of global digital marketing requires
          a thoughtful and well-informed approach, combining an understanding of
          each market and analysis of your brand’s potential, to set the right
          strategy.
        </p>
        <p>
          A localized approach to digital marketing, that goes beyond copy
          translation, helps brands run efficient campaigns to increase demand
          and accelerate market growth.
        </p>
        <p>
          We hope you found these tips and best practices useful. Contact us to
          hear more about Global-e's end-to-end global e-commerce solutions.
        </p>
      </div>
      <div class="link-con">
        <p>
          <sup>1</sup> Hubspot, 2024
          <a
            href="https://blog.hubspot.com/marketing/hubspot-blog-marketing-industry-trends-report?hubs_content=blog.hubspot.com%2Fmarketing%2Fmarketing-channels&amp;hubs_content-cta=survey"
            target="_blank"
            class="link-22"
            >The 2024 State of Marketing &amp; Trends Report: Data from 1400+
            Global Marketers</a
          >; Statista, 2023
          <a
            href="https://www.statista.com/statistics/188447/influence-of-global-social-media-marketing-usage-on-businesses/"
            target="_blank"
            class="link-22"
            >Leading benefits of using social media for marketing purposes
            worldwide</a
          >
        </p>
        <p>
          <sup>2</sup> SEMRush, 2024
          <a
            href="https://www.semrush.com/blog/customer-acquisition-guide/"
            target="_blank"
            class="link-22"
            >Customer Acquisition: How to Win New Customers</a
          >
        </p>
        <p>
          <sup>3</sup> Global-e, 2022
          <a
            href="https://assets.website-files.com/624c0935e5c07e7129755fff/63e50d399545f25d36105485_The Global Cross-border ecommerce shopper Survey_Global-e.pdf"
            target="_blank"
            class="link-22"
            >The Cross-Border E-commerce Shopper Survey</a
          >
        </p>
        <p>
          <sup>4</sup> Wikipedia,
          <a
            href="https://en.wikipedia.org/wiki/KakaoTalk"
            target="_blank"
            class="link-22"
            >https://en.wikipedia.org/wiki/KakaoTalk</a
          >
        </p>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  data() {
    return {}
  },
  methods: {}
}
</script>

<style scoped lang="less">
.detail {
  background-color: #fff;

  .detail-con {
    max-width: 1200px;
    margin: 0 auto;

    .section1 {
      height: 950px;
      width: 100%;
      background-image: linear-gradient(#fffffff0, #fffffff0),
        url('../../assets/knowledge-center/center_bg.png');
      background-position: 0 0, 50% 120%;
      background-repeat: repeat, no-repeat;
      background-size: auto, auto;

      .tips {
        width: 981px;
        max-width: 900px;
        margin: 0 auto;
        padding-top: 28px;
        padding-left: 30px;

        img {
          max-height: 35px;
          margin-top: 50px;
        }

        .heading {
          color: #999;
          font-size: 55px;
          margin-bottom: 25px;
          line-height: 1.1;
          font-weight: 400;
          padding-top: 30px;
          width: 600px;

          span {
            color: #f15a2b;
            font-weight: 600;
            font-size: 55px;
            line-height: 1.1;
            padding-bottom: 30px;
          }
        }

        p {
          font-size: 18px;
          line-height: 1.6;
          margin-bottom: 10px;
        }
      }
    }

    .section2 {
      margin-top: 80px;
      padding-bottom: 100px;

      .words {
        width: 981px;
        max-width: 900px;
        padding-left: 30px;
        margin: 0 auto;
        position: relative;
        z-index: 0;

        .title {
          color: #e6e6e680;
          font-size: 150px;
          position: absolute;
          top: -155px;
          left: -96px;
          font-weight: 100;
          font-family: sans-serif;
          z-index: -1;
        }
        .ti1 {
          top: -165px;
          left: -136px;
        }

        .ti-con {
          .ti-top {
            border-bottom: 4px solid #f15a2b;
            font-size: 32px;
            font-weight: 700;
            line-height: 36px;
            display: inline-block;
          }

          p {
            font-size: 18px;
            line-height: 36px;
            padding-top: 15px;
          }

          .hTi {
            color: #f15a2b;
            font-size: 22px;
            font-weight: 600;
            line-height: 30px;
            padding-bottom: 10px;
          }

          ul {
            list-style-type: square;
            padding-left: 40px;
            margin-bottom: 10px;

            li {
              color: #f15a2b;
              margin-bottom: 11px;
            }
          }
        }
      }
    }

    .section3 {
      display: flex;
      width: 960px;
      margin: 60px auto 0;

      .words {
        width: 600px;

        .ti-con {
          width: 600px;

          p {
            line-height: 26px;
          }
        }
      }

      .right-con {
        border: 4px solid #e6e6e6;
        padding: 24px 20px;
        border-radius: 25px;
        margin-left: 40px;

        h3 {
          margin-top: 10px;
          font-weight: 600;
          color: #191919;
          font-size: 24px;
        }

        img {
          padding: 10px;
        }

        p {
          font-size: 16px;
          color: #8f8f8f;
          line-height: 24px;
          padding-top: 10px;
        }
      }

      .right-img {
        border: none;
        width: 220px;

        img {
          max-height: 350px;
          width: 220px;
          box-shadow: 0 0 15px #0003;
          margin-bottom: 20px;
        }
      }
    }
    .alone {
      width: 900px;
      margin: 0 auto;
      p {
        padding-left: 20px;
        color: #333;
        font-size: 18px;
        line-height: 30px;
      }
    }
    .link-con {
      width: 870px;
      margin: 0 auto;
      margin-top: 60px;
      p {
        font-size: 14px;
        line-height: 20px;
        padding-top: 10px;
        color: #333;
        font-style: italic;
        sup {
          position: relative;
          top: -5px;
        }
        a {
          color: #333;
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
